import { useForm } from '@mantine/form';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import { RecurrenceValues } from 'src/Hooks/Forms/UseCreateEventForm';
import { transformFormValuesToRecurrenceDto } from 'src/Hooks/Forms/UseCreateEventForm/transformFormValuesToRecurrenceDto';
import {
  CreatePracticalInformationDto,
  CreatePracticalInformationDtoSubtypeEnum,
  CreatePracticalInformationDtoTypeEnum,
  CreatePracticalInformationFieldDtoFieldTypeEnum,
} from 'src/Services/API/generated';

interface PracticalInformationField {
  value: Array<string> | RecurrenceValues | Date | string | undefined;
  key: string;
  fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum;
}

export interface CreatePracticalInformationFormValues {
  title: string;
  type: CreatePracticalInformationDtoTypeEnum;
  subtype?: CreatePracticalInformationDtoSubtypeEnum;
  icon?: string;
  fields: PracticalInformationField[];
  reminderFieldKey?: string;
  reminderDurationSeconds?: number;
  reminderTimeOfDay?: string;
  attachments: FormAttachment[];
}

export const useCreatePracticalInformationForm = (
  initialValues?: Partial<CreatePracticalInformationFormValues>
) => {
  const { t } = useTranslation();

  const form = useForm<
    CreatePracticalInformationFormValues,
    (values: CreatePracticalInformationFormValues) => CreatePracticalInformationDto
  >({
    initialValues: {
      title: '',
      subtype: undefined,
      type: CreatePracticalInformationDtoTypeEnum.GeneralInformation,
      icon: undefined,
      fields: [],
      reminderFieldKey: undefined,
      reminderDurationSeconds: undefined,
      reminderTimeOfDay: undefined,
      attachments: [],
      ...initialValues,
    },

    validate: {
      title: (value, values) => {
        if (values.subtype !== undefined) return null;

        if (!value) return t('practicalInformation.titleRequired', 'Le titre est requis');
        if (value.length < 3)
          return t(
            'practicalInformation.titleNeedsAtLeast3Characters',
            'Le titre doit contenir au moins 3 caractères'
          );
        return null;
      },
      type: value => {
        if (!Object.values(CreatePracticalInformationDtoTypeEnum).includes(value)) {
          return t('practicalInformation.invalidType', 'Type invalide');
        }
        return null;
      },
      fields: {
        value: (value, values, path) => {
          const index = parseInt(path.split('.')[1]);
          const allFields = values.fields ?? [];

          let isCurrentValueValid = false;

          if (Array.isArray(value)) {
            isCurrentValueValid = value.length > 0;
          } else if (typeof value === 'string') {
            isCurrentValueValid = value.trim() !== '';
          } else if (
            value instanceof Date ||
            (value && typeof value === 'object' && 'isValid' in value)
          ) {
            isCurrentValueValid = true;
          } else if (value?.['timezone']) {
            isCurrentValueValid = value['frequency'] !== undefined;
          }

          const hasAtLeastOneValue = allFields.some((field, fieldIndex) => {
            if (fieldIndex === index) {
              return isCurrentValueValid;
            }

            const fieldValue = field.value;
            if (Array.isArray(fieldValue)) {
              return fieldValue.length > 0;
            } else if (typeof fieldValue === 'string') {
              return fieldValue.trim() !== '';
            } else if (
              fieldValue instanceof Date ||
              (fieldValue && typeof fieldValue === 'object' && 'isValid' in fieldValue)
            ) {
              return true;
            } else if (fieldValue?.['timezone']) {
              return fieldValue['frequency'] !== undefined;
            }

            return false;
          });

          if (!isCurrentValueValid && !hasAtLeastOneValue) {
            return t(
              'practicalInformation.atLeastOneFieldRequired',
              'Au moins un champ doit être rempli'
            );
          }

          return null;
        },
        key: value => (!value ? t('practicalInformation.keyRequired', 'La clé est requise') : null),
      },
    },
    transformValues: (
      values: CreatePracticalInformationFormValues
    ): CreatePracticalInformationDto => {
      const { subtype, title, fields, attachments: _attachments, ...info } = values;

      const dtoFields = fields
        .filter(
          field =>
            field.value !== undefined &&
            field.value !== null &&
            field.value !== '' &&
            (field.fieldType !== CreatePracticalInformationFieldDtoFieldTypeEnum.Schedule ||
              (field.value as RecurrenceValues).frequency)
        )
        .map(field => {
          if (field.fieldType === CreatePracticalInformationFieldDtoFieldTypeEnum.Schedule) {
            return {
              fieldType: field.fieldType,
              value: transformFormValuesToRecurrenceDto(field.value as RecurrenceValues),
              key: field.key,
            };
          }
          return {
            fieldType: field.fieldType,
            value: field.value as string | string[] | Dayjs,
            key: field.key,
          };
        });

      if (subtype) {
        return {
          ...info,
          fields: dtoFields,
          subtype,
        };
      } else {
        return {
          ...info,
          fields: dtoFields,
          title,
        };
      }
    },
  });

  return form;
};
